"use client";

import { useMemo, useState } from "react";
import {
  Field,
  TextInput,
  SelectInput,
  CheckboxRow,
  SegmentedControl,
  ResultBanner,
  PrintButton,
  PrintFooter,
  trackToolResult,
  type Verdict,
} from "@/components/tools/ui";

/* Drafts a Legionella risk assessment for a typical hot & cold water system, around
   the HSE Approved Code of Practice L8 (4th ed) and the technical guidance HSG274
   Part 2. Legionella is a COSHH hazardous substance; the duty to assess sits with
   the dutyholder under the Health and Safety at Work etc. Act 1974.

   Compliance (YMYL): a self-help record for simple hot & cold water systems only.
   Cooling towers/evaporative condensers, spa pools and healthcare premises (HTMs)
   need competent specialist help — the tool flags this and is not a substitute. */

type State = "" | "ok" | "action" | "na";

const RISK_FACTORS = [
  "Water stored or distributed between 20°C and 45°C (ideal for Legionella)",
  "Stored water — cold tanks, calorifiers or hot-water cylinders",
  "Dead legs or little-used outlets where water stagnates",
  "Showers, spray taps or other outlets that create a breathable aerosol",
  "Areas or outlets used infrequently (void units, seasonal areas)",
  "Susceptible people on site (elderly, unwell, immunocompromised)",
];

interface CItem { id: string; label: string; critical?: boolean }
const CONTROLS: { id: string; title: string; items: CItem[] }[] = [
  {
    id: "temp", title: "Temperature control",
    items: [
      { id: "hot-store", label: "Hot water stored at ≥ 60°C (calorifier/cylinder)", critical: true },
      { id: "hot-out", label: "Hot water reaches ≥ 50°C at the outlet within ~1 minute" },
      { id: "cold", label: "Cold water stays below 20°C at the outlet", critical: true },
      { id: "tmv", label: "Scald risk from hot water controlled (e.g. TMVs where needed)" },
    ],
  },
  {
    id: "manage", title: "System management",
    items: [
      { id: "flush", label: "Little-used outlets flushed weekly (and after void periods)", critical: true },
      { id: "showers", label: "Shower heads and spray outlets cleaned and descaled (≈quarterly)" },
      { id: "tanks", label: "Cold tanks and calorifiers inspected; lids/insect screens in place" },
      { id: "deadlegs", label: "Dead legs and redundant pipework removed where practicable" },
    ],
  },
  {
    id: "duty", title: "Dutyholder duties (ACoP L8)",
    items: [
      { id: "scheme", label: "A written scheme of control is in place and followed", critical: true },
      { id: "monitor", label: "Temperatures and tasks monitored and recorded" },
      { id: "rp", label: "A responsible person is appointed and competent" },
    ],
  },
];
const ALL = CONTROLS.flatMap((c) => c.items);

const OPTIONS = [
  { value: "ok" as const, label: "In place", activeCls: "border-emerald bg-emerald-tint text-emerald-deep" },
  { value: "action" as const, label: "Action", activeCls: "border-risk-veryhigh bg-risk-veryhigh/10 text-risk-veryhigh" },
  { value: "na" as const, label: "N/A", activeCls: "border-line-2 bg-mist text-muted" },
];

export function LegionellaRiskAssessment() {
  const [premises, setPremises] = useState("");
  const [rp, setRp] = useState("");
  const [system, setSystem] = useState("hot-cold");
  const [factors, setFactors] = useState<Record<string, boolean>>({});
  const [ctrl, setCtrl] = useState<Record<string, State>>({});

  const escalate = system === "cooling" || system === "spa";

  const result = useMemo(() => {
    const answered = ALL.filter((i) => ctrl[i.id]);
    const actions = ALL.filter((i) => ctrl[i.id] === "action");
    const critical = actions.filter((i) => i.critical);
    const remaining = ALL.length - answered.length;
    let level: Verdict = "low";
    let title = "";
    if (escalate) { level = "veryhigh"; title = "This system needs a specialist — see below"; }
    else if (answered.length === 0) title = "Work through the control measures below";
    else if (critical.length > 0) { level = "veryhigh"; title = `${critical.length} critical control${critical.length > 1 ? "s" : ""} missing — act now`; }
    else if (actions.length > 0) { level = "medium"; title = `${actions.length} action${actions.length > 1 ? "s" : ""} to complete`; }
    else if (remaining === 0) { level = "low"; title = "Controls in place on this checklist — keep monitoring"; }
    else { level = "low"; title = `${answered.length}/${ALL.length} reviewed`; }
    return { actions, critical, remaining, answered: answered.length, level, title };
  }, [ctrl, escalate]);

  if (result.remaining === 0 && result.answered > 0 && !escalate) trackToolResult("legionella-risk-assessment", { actions: result.actions.length, critical: result.critical.length });

  return (
    <div className="space-y-8">
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Premises / address"><TextInput value={premises} onChange={(e) => setPremises(e.target.value)} placeholder="e.g. 14 High Street flats" /></Field>
        <Field label="Dutyholder / responsible person"><TextInput value={rp} onChange={(e) => setRp(e.target.value)} placeholder="Landlord / employer / FM" /></Field>
        <Field label="Water system type" className="sm:col-span-2">
          <SelectInput value={system} onChange={(e) => setSystem(e.target.value)}>
            <option value="hot-cold">Hot &amp; cold water system (typical building)</option>
            <option value="cooling">Cooling tower / evaporative condenser</option>
            <option value="spa">Spa pool / hot tub</option>
            <option value="healthcare">Healthcare premises (HTM standards apply)</option>
          </SelectInput>
        </Field>
      </div>

      {escalate && (
        <ResultBanner level="veryhigh" title="This system needs competent specialist help">
          <p>{system === "cooling" ? "Cooling towers and evaporative condensers are higher-risk, must be notified to the local authority, and need a competent water-treatment specialist (HSG274 Part 1)." : "Spa pools and hot tubs are higher-risk aerosol systems needing specialist management (HSG282)."} This tool covers simple hot &amp; cold water systems only — don't rely on it for this system.</p>
        </ResultBanner>
      )}
      {system === "healthcare" && (
        <ResultBanner level="medium" title="Healthcare premises — HTM standards apply">
          <p>Healthcare water systems are assessed to NHS Health Technical Memoranda (e.g. HTM 04-01), which go beyond ACoP L8. Use a competent water-safety adviser; this checklist is a starting point only.</p>
        </ResultBanner>
      )}

      {result.answered > 0 && !escalate && (
        <ResultBanner level={result.level} title={result.title}>
          {result.critical.length > 0 ? <p>A critical control is missing — temperature, flushing or the written scheme. Legionella thrives when these slip. Act now and record it.</p>
            : result.actions.length > 0 ? <p>Some controls need action. Record them with an owner and date, and bring them into the written scheme of control.</p>
            : <p>Controls look to be in place. Keep monitoring and recording temperatures and tasks, and review the assessment after any change to the system or its use.</p>}
        </ResultBanner>
      )}

      {/* Risk factors */}
      <fieldset className="rounded-2xl border border-line bg-white p-5 shadow-soft">
        <legend className="px-2 font-display text-base font-bold text-ink">What raises the risk here</legend>
        <div className="mt-3 space-y-2.5">
          {RISK_FACTORS.map((label, i) => <CheckboxRow key={i} checked={!!factors[`f${i}`]} onChange={(v) => setFactors((s) => ({ ...s, [`f${i}`]: v }))}>{label}</CheckboxRow>)}
        </div>
      </fieldset>

      {/* Controls */}
      {CONTROLS.map((g) => (
        <fieldset key={g.id} className="rounded-2xl border border-line bg-white p-5 shadow-soft">
          <legend className="px-2 font-display text-base font-bold text-ink">{g.title}</legend>
          <div className="mt-3 space-y-4">
            {g.items.map((item) => (
              <div key={item.id} className="grid gap-2 border-b border-line pb-4 last:border-0 last:pb-0 sm:grid-cols-[1fr_auto] sm:items-start sm:gap-4">
                <p className="text-sm text-body">{item.label}{item.critical && <span className="ml-2 rounded bg-risk-veryhigh/10 px-1.5 py-0.5 text-[0.6rem] font-semibold uppercase tracking-wider text-risk-veryhigh">Critical</span>}</p>
                <SegmentedControl legend={item.label} name={item.id} value={ctrl[item.id] ?? ""} onChange={(v) => setCtrl((s) => ({ ...s, [item.id]: v as State }))} options={OPTIONS} className="sm:w-[250px]" />
              </div>
            ))}
          </div>
        </fieldset>
      ))}

      {result.actions.length > 0 && !escalate && (
        <div className="rounded-2xl border border-amber-200 bg-amber-50 p-5">
          <h3 className="font-display text-base font-bold text-amber-900">Actions {premises && `· ${premises}`}</h3>
          <ul className="mt-3 space-y-2 text-sm text-amber-900">
            {result.actions.map((a) => <li key={a.id} className="flex gap-2"><span className="mt-0.5 shrink-0 font-bold">{a.critical ? "‼" : "•"}</span><span>{a.label}{a.critical && " (critical)"}</span></li>)}
          </ul>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-3">
        <PrintButton tool="legionella-risk-assessment" label="Print / save this assessment" />
        <span className="text-xs text-muted">{result.answered}/{ALL.length} controls reviewed</span>
      </div>
      <PrintFooter toolName="Legionella Risk Assessment" />
    </div>
  );
}
